/**
 * Error Boundary System - Granular Error Handling
 *
 * Provides multiple error boundary variants:
 * - ErrorBoundary: Global/page-level with full error UI
 * - SectionErrorBoundary: Card/section-level with compact inline recovery
 * - SilentErrorBoundary: Logs errors but renders null (for non-critical features)
 *
 * Integrates with Sentry for production error tracking
 */

import React, { Component, type ReactNode, memo } from 'react';
import { AlertTriangle, RefreshCw, Home, AlertCircle } from '@/lib/icons';
import { Button } from './Button';
import { captureException } from '@/lib/errorTracking';
import { triggerHaptic } from '@/lib/haptics';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Component/section name for better error tracking */
  name?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global Error Boundary - Full page error UI
 * Use at route/page level for catastrophic failures
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log sanitized error info only (no PII/health data)
    console.error('ErrorBoundary caught an error:', {
      name: error.name,
      message: error.message?.substring(0, 100) // Truncate to avoid large PII
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);

    // Send to error tracking service (Sentry when configured)
    captureException(error, {
      component: this.props.name || 'ErrorBoundary',
      action: 'componentDidCatch',
      extra: {
        componentStack: errorInfo.componentStack,
        digest: errorInfo.digest,
      },
    });
  }

  handleRetry = (): void => {
    triggerHaptic();
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = (): void => {
    triggerHaptic();
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI - Premium Apple-inspired
      return (
        <div className="min-h-dvh bg-gradient-to-b from-white to-gray-50 flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center motion-safe:animate-fade-in">
            {/* Error icon with premium styling */}
            <div className="w-20 h-20 mx-auto mb-6 rounded-[22px] bg-gradient-to-br from-amber-100 to-orange-100
                           flex items-center justify-center shadow-[0_4px_16px_rgba(245,158,11,0.2)]">
              <AlertTriangle className="w-10 h-10 text-amber-600" />
            </div>

            {/* Error message with Apple-style typography */}
            <h1 className="text-title font-bold text-gray-900 mb-3 tracking-[-0.02em]">
              Something went wrong
            </h1>
            <p className="text-body text-gray-500 mb-8 leading-relaxed">
              We're sorry, but something unexpected happened.
              Don't worry — your data is safe.
            </p>

            {/* Action buttons - 44pt minimum touch targets */}
            <div className="flex flex-col gap-3">
              <Button
                onClick={this.handleRetry}
                variant="primary"
                fullWidth
                icon={<RefreshCw className="w-4 h-4" />}
              >
                Try Again
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="secondary"
                fullWidth
                icon={<Home className="w-4 h-4" />}
              >
                Go Home
              </Button>
            </div>

            {/* Error details (development only) */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700
                                   min-h-[44px] flex items-center justify-center">
                  Technical details
                </summary>
                <pre className="mt-2 p-4 bg-gray-100 rounded-[14px] text-xs text-gray-700
                               overflow-auto max-h-40 font-mono">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Section Error Boundary - Compact inline error UI
 * Use for cards, widgets, and non-critical sections
 */
interface SectionErrorBoundaryProps extends ErrorBoundaryProps {
  /** Compact mode for smaller sections */
  compact?: boolean;
}

export class SectionErrorBoundary extends Component<SectionErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log sanitized error info only (no PII/health data) - error codes only in production
    console.error(`[${this.props.name || 'Section'}] Error:`, { name: error.name, message: error.message?.substring(0, 100) });

    this.props.onError?.(error, errorInfo);

    captureException(error, {
      component: this.props.name || 'SectionErrorBoundary',
      action: 'componentDidCatch',
      extra: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  handleRetry = (): void => {
    triggerHaptic();
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Compact inline error UI
      return (
        <div className={`flex items-center justify-center text-center
                        bg-amber-50/50 border border-amber-200/50 rounded-[16px]
                        ${this.props.compact ? 'p-4' : 'p-6'}`}
             role="alert"
             aria-live="polite">
          <div>
            <AlertCircle className={`mx-auto text-amber-500 mb-2
                                    ${this.props.compact ? 'w-5 h-5' : 'w-6 h-6'}`} />
            <p className={`text-gray-700 font-medium mb-2
                         ${this.props.compact ? 'text-label-sm' : 'text-body-sm'}`}>
              Unable to load
            </p>
            <button
              onClick={this.handleRetry}
              className="text-primary-600 hover:text-primary-700 font-medium
                        min-h-[44px] px-4 inline-flex items-center gap-1.5
                        hover:bg-primary-50 rounded-chip motion-safe:transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Retry
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Silent Error Boundary - Logs but renders nothing
 * Use for optional/decorative features that shouldn't break the page
 */
export class SilentErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true, error: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log sanitized error info only (no PII/health data)
    if (import.meta.env.DEV) {
      console.warn(`[${this.props.name || 'Silent'}] Non-critical error suppressed:`, error);
    }

    this.props.onError?.(error, errorInfo);

    // Still track in Sentry but with lower severity
    captureException(error, {
      component: this.props.name || 'SilentErrorBoundary',
      action: 'componentDidCatch',
      extra: {
        componentStack: errorInfo.componentStack,
        severity: 'warning',
      },
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Render fallback or nothing
      return this.props.fallback || null;
    }
    return this.props.children;
  }
}

/**
 * ErrorFallback - Reusable error display component
 * For use outside of error boundaries or as custom fallback
 */
interface ErrorFallbackProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  variant?: 'page' | 'section' | 'inline';
}

export const ErrorFallback = memo(function ErrorFallback({
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try again.',
  onRetry,
  variant = 'section',
}: ErrorFallbackProps) {
  const handleRetry = () => {
    triggerHaptic();
    onRetry?.();
  };

  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-2 text-amber-600 text-sm py-2" role="alert">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{message}</span>
        {onRetry && (
          <button
            onClick={handleRetry}
            className="underline hover:no-underline ml-1 min-h-[44px] inline-flex items-center"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  if (variant === 'section') {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center
                     bg-gray-50 rounded-card border border-border" role="alert">
        <AlertCircle className="w-8 h-8 text-amber-500 mb-3" />
        <h3 className="text-body font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-label-sm text-gray-500 mb-4">{message}</p>
        {onRetry && (
          <Button onClick={handleRetry} variant="secondary" size="sm" icon={<RefreshCw className="w-4 h-4" />}>
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // Page variant handled by ErrorBoundary default UI
  return null;
});
